import React, { useRef, useMemo } from "react";
import moment from "moment";
import { useFormContext } from "react-hook-form";
import { DayContentProps } from "react-day-picker";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import { getHolidays } from "api/services/common";
import { isAbortError } from "helpers/zipcodeGeocode";
import DatePicker, { DatePickerProps } from "components/atoms/DatePicker/DatePicker";

function DayContentCustom(props: DayContentProps & { holidays: any; countryCode: any }) {
  const { date, activeModifiers, holidays, countryCode } = props;

  const currentDateHolidayData = useMemo(() => {
    const month = moment(date).month() + 1;
    const year = moment(date).year();
    return holidays?.[countryCode]?.[year]?.[month]?.find((item: any) => item.date === moment(date).format("YYYY-MM-DD"));
  }, [holidays, date, countryCode]);

  return (
    <>
      {activeModifiers?.holiday && currentDateHolidayData ? (
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id="" style={{ zIndex: 999999999999 }}>
              <span className="font-weight-normal">Holiday Warning: It might be {currentDateHolidayData?.name || ""}</span>
            </Tooltip>
          }
        >
          <div>
            <div className="custom-itentifier" />
            <span>{moment(date).format("D")}</span>
          </div>
        </OverlayTrigger>
      ) : (
        <span>{moment(date).format("D")}</span>
      )}
    </>
  );
}

type PickupDeliveryDateType = {
  datePickerProps: DatePickerProps;
  isPickupOrDelivery?: string;
  countries: any;
  holidays: any;
  setHolidays: React.Dispatch<React.SetStateAction<any>>;
  onDateSelect?: any;
  countryCode?: string;
};

function cacheHolidayMonth(prev: any, countryCode: string, year: number, month: number, data: unknown[]) {
  return {
    ...prev,
    [countryCode]: {
      ...prev[countryCode],
      [year]: {
        ...prev[countryCode]?.[year],
        [month]: data,
      },
    },
  };
}

function PickupDeliveryDate(props: PickupDeliveryDateType) {
  const {
    datePickerProps,
    isPickupOrDelivery,
    countries,
    holidays,
    setHolidays,
    onDateSelect,
    countryCode: countryCodeProp,
  } = props;
  const holidaysAbortController = useRef<AbortController>();
  const { getValues } = useFormContext();

  /**
   * TODO: not the best approch but current country implimentation has bugs
   * and its the best we can do for now, will have to refector how country
   * is managed.
   */
  const getCountryCode = () => {
    const explicitCountryCode = countryCodeProp?.trim().toUpperCase();
    if (explicitCountryCode) {
      return explicitCountryCode;
    }
    let countryCode: string = "";
    const countryName =
      isPickupOrDelivery === "pickup" ? getValues("schedule.pickup_country") : getValues("schedule.stop1_country");
    const checkCountry = countries.find(
      (country: any) => country.label?.toLowerCase() === countryName?.trim()?.toLowerCase(),
    );
    if (checkCountry?.value) {
      countryCode = checkCountry.value;
    } else if (countryName?.length <= 3) {
      countryCode = countryName;
    }
    return countryCode;
  };

  const getHolidayData = (date: Date) => {
    const month = moment(date).month() + 1;
    const year = moment(date).year();
    if (holidaysAbortController.current) {
      holidaysAbortController.current.abort();
    }
    const countryCode = getCountryCode();
    if (!countryCode) {
      return;
    }
    if (holidays?.[countryCode]?.[year]?.[month]) {
      return;
    }
    holidaysAbortController.current = new AbortController();
    getHolidays({
      params: {
        country: countryCode,
        year,
        month,
        language: "en",
      },
      signal: holidaysAbortController.current.signal,
    })
      .then((res) => {
        setHolidays((prev: any) => cacheHolidayMonth(prev, countryCode, year, month, res.data ?? []));
      })
      .catch((err) => {
        if (isAbortError(err)) {
          return;
        }
        if (err?.response?.status === 404) {
          setHolidays((prev: any) => cacheHolidayMonth(prev, countryCode, year, month, []));
        }
      });
  };

  const getHolidayDates = (day: Date) => {
    const countryCode = getCountryCode();
    const month = moment(day).month() + 1;
    const year = moment(day).year();

    if (holidays?.[countryCode]?.[year]?.[month]) {
      return holidays[countryCode][year][month].find((item: any) => item.date === moment(day).format("YYYY-MM-DD"));
    }
    return false;
  };

  const handleMonthChange = (date?: Date) => {
    if (date) {
      getHolidayData(date);
    }
  };

  const handlePickerOpen = (date: Date) => {
    getHolidayData(date);
  };

  return (
    <div>
      <DatePicker
        {...datePickerProps}
        onMonthChange={handleMonthChange}
        onOpen={handlePickerOpen}
        isLoading={false}
        modifiers={{
          holiday: (day: Date) => getHolidayDates(day),
        }}
        components={{
          DayContent: (data) => <DayContentCustom {...data} holidays={holidays} countryCode={getCountryCode()} />,
        }}
        onDateSelect={onDateSelect}
      />
    </div>
  );
}

export default PickupDeliveryDate;

PickupDeliveryDate.defaultProps = {
  isPickupOrDelivery: "",
  onDateSelect: () => {},
};
